import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ImageOff, Plus, Check, FolderOpen } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";

const inputCls =
  "bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-md px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export const GalleryManager = () => {
  const [cats, setCats] = useState(null);
  const [items, setItems] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [edits, setEdits] = useState({});
  const [uploadCat, setUploadCat] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [{ data: c }, { data: g }] = await Promise.all([api.get("/gallery/categories"), api.get("/gallery")]);
    setCats(c);
    setItems(g);
  };
  useEffect(() => { load().catch(() => setCats([])); }, []);

  const addCategory = async () => {
    if (!newCat.trim()) return toast.error("Masukkan nama kategori");
    await api.post("/gallery/categories", { name: newCat.trim() });
    toast.success("Kategori ditambah");
    setNewCat("");
    load();
  };

  const renameCategory = async (id) => {
    const name = (edits[id] || "").trim();
    if (!name) return;
    await api.patch(`/gallery/categories/${id}`, { name });
    toast.success("Nama kategori dikemas kini");
    setEdits((p) => ({ ...p, [id]: undefined }));
    load();
  };

  const deleteCategory = async (id) => {
    await api.delete(`/gallery/categories/${id}`);
    toast.success("Kategori dan gambarnya dipadam");
    load();
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!uploadCat) return toast.error("Sila pilih kategori dahulu");
    if (!file) return toast.error("Sila pilih fail imej dahulu");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("category_id", uploadCat);
      await api.post("/gallery", fd);
      toast.success("Gambar berjaya dimuat naik!");
      setCaption("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (id) => {
    await api.delete(`/gallery/${id}`);
    toast.success("Gambar dipadam");
    load();
  };

  if (cats === null) return <p className="text-slate-500 dark:text-slate-400">Memuatkan...</p>;

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const shown = filterCat ? items.filter((i) => i.category_id === filterCat) : items;

  return (
    <div className="space-y-8" data-testid="gallery-manager">
      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-6 space-y-4 shadow-sm dark:shadow-none" data-testid="gallery-categories-panel">
        <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-lg flex items-center gap-2">
          <FolderOpen size={18} className="text-orange-500" /> Kategori Galeri
        </h3>
        <div className="space-y-2">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-2" data-testid={`gallery-category-row-${c.id}`}>
              <input
                className={`${inputCls} flex-1`}
                value={edits[c.id] !== undefined ? edits[c.id] : c.name}
                onChange={(e) => setEdits((p) => ({ ...p, [c.id]: e.target.value }))}
                data-testid={`category-name-input-${c.id}`}
              />
              <span className="text-slate-500 text-xs w-20">{c.count} gambar</span>
              {edits[c.id] !== undefined && edits[c.id] !== c.name && (
                <button
                  onClick={() => renameCategory(c.id)}
                  data-testid={`save-category-btn-${c.id}`}
                  className="flex items-center gap-1 text-xs bg-emerald-800/60 hover:bg-emerald-700 text-emerald-200 px-3 py-2 rounded-full transition-colors"
                >
                  <Check size={13} /> Simpan
                </button>
              )}
              <button
                onClick={() => deleteCategory(c.id)}
                data-testid={`delete-category-btn-${c.id}`}
                className="flex items-center gap-1 text-xs bg-red-900/40 hover:bg-red-900/70 text-red-300 px-3 py-2 rounded-full transition-colors"
              >
                <Trash2 size={13} /> Padam
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
          <input
            className={`${inputCls} flex-1`}
            placeholder="Nama kategori baharu (cth: TMG - STORE AND WAREHOUSE)"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            data-testid="new-category-input"
          />
          <button
            onClick={addCategory}
            data-testid="add-category-btn"
            className="flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
          >
            <Plus size={15} /> Tambah
          </button>
        </div>
        <p className="text-slate-500 text-xs">Padam kategori akan memadam semua gambar di dalamnya.</p>
      </div>

      <form onSubmit={upload} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-6 space-y-4 shadow-sm dark:shadow-none" data-testid="gallery-upload-form">
        <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-lg">Muat Naik Gambar Baharu</h3>
        <select
          className={`${inputCls} w-full`}
          value={uploadCat}
          onChange={(e) => setUploadCat(e.target.value)}
          data-testid="upload-category-select"
        >
          <option value="">— Pilih Kategori —</option>
          {cats.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          data-testid="gallery-file-input"
          onChange={(e) => setFile(e.target.files[0] || null)}
          className="block w-full text-sm text-slate-500 dark:text-slate-400 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:bg-orange-600 file:text-white file:font-semibold file:text-sm hover:file:bg-orange-700 file:cursor-pointer"
        />
        <input
          data-testid="gallery-caption-input"
          placeholder="Kapsyen gambar (pilihan)"
          className={`${inputCls} w-full`}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <button
          type="submit"
          disabled={uploading}
          data-testid="gallery-upload-btn"
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full transition-all"
        >
          <Upload size={16} /> {uploading ? "Memuat naik..." : "Muat Naik"}
        </button>
        <p className="text-slate-500 text-xs">Format: JPG, PNG, WEBP, GIF · Maksimum 8MB</p>
      </form>

      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-lg">Semua Gambar</h3>
          <select
            className={inputCls}
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            data-testid="filter-category-select"
          >
            <option value="">Semua Kategori</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {shown.length === 0 ? (
          <div className="bg-slate-900/60 border border-white/10 rounded-xl p-16 text-center" data-testid="admin-gallery-empty">
            <ImageOff className="text-slate-600 mx-auto mb-4" size={40} />
            <p className="text-slate-400">Tiada gambar. Muat naik gambar pertama anda!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-gallery-grid">
            {shown.map((item) => (
              <div key={item.id} className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden" data-testid={`admin-gallery-item-${item.id}`}>
                <img src={fileUrl(item.url)} alt={item.caption || "Galeri"} className="w-full aspect-[4/3] object-cover" />
                <div className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-300 text-sm truncate">{item.caption || <em className="text-slate-500">Tiada kapsyen</em>}</p>
                    <p className="text-orange-400/80 text-xs mt-0.5 truncate">{catName(item.category_id)}</p>
                  </div>
                  <button
                    onClick={() => deleteImage(item.id)}
                    data-testid={`delete-gallery-btn-${item.id}`}
                    className="flex items-center gap-1.5 text-xs bg-red-900/40 hover:bg-red-900/70 text-red-300 px-3 py-2 rounded-full transition-colors shrink-0"
                  >
                    <Trash2 size={13} /> Padam
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
